import { Button, HTMLInputProps, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import React from 'react'
import Client from '../../client'
import { isSolutionsQRCodePayload, sanitizeOperationalSku } from '../../utils'
import { showToast } from '../../Toast'

interface Props {
  inputProps?: HTMLInputProps
  value?: string
  supplierId?: number
  placeholder?: string
  onChange: (string) => void
  parseQRCodePayloads?: boolean
  onParseLoading?: (loading: boolean) => void
}

const QR_CODE_MAX_KEY_PRESS_INTERVAL = 1000

const BarcodeInput: React.FC<Props & HTMLInputProps> = ({
  inputProps = {},
  value,
  supplierId,
  placeholder,
  onChange,
  parseQRCodePayloads,
  onParseLoading,
  ...props
}) => {
  const [loading, setLoading] = React.useState(null)
  const qrCodeParseRequestId = React.useRef(0)
  const recentQRCodeInput = React.useRef('')
  const lastQRCodeKeyPressTimestamp = React.useRef(null)

  const setParseLoading = React.useCallback(
    (nextLoading: boolean) => {
      setLoading(nextLoading)
      onParseLoading?.(nextLoading)
    },
    [onParseLoading]
  )

  const parseQRCode = React.useCallback(
    async (payload: string, requestId: number) => {
      if (requestId !== qrCodeParseRequestId.current) return

      try {
        const res = await Client.parseQRCode(payload)
        if (requestId !== qrCodeParseRequestId.current) return

        onChange(sanitizeOperationalSku(res.data.qrCode || res.data.qr_code))
      } catch (e) {
        if (requestId !== qrCodeParseRequestId.current) return

        showToast(e.response?.data?.message || 'Failed to parse QR code', Intent.WARNING, false)
      } finally {
        if (requestId === qrCodeParseRequestId.current) {
          setParseLoading(false)
        }
      }
    },
    [onChange, setParseLoading]
  )

  const enqueueQRCodeParse = React.useCallback(
    (payload: string) => {
      const requestId = qrCodeParseRequestId.current + 1
      qrCodeParseRequestId.current = requestId
      setParseLoading(true)
      parseQRCode(payload, requestId)
    },
    [parseQRCode, setParseLoading]
  )

  const shouldParseSolutionsQRCodePayload = React.useCallback(
    (payload: string): boolean => !!parseQRCodePayloads && isSolutionsQRCodePayload(payload) && payload.includes('|'),
    [parseQRCodePayloads]
  )

  const changeHandler = rawValue => {
    if (parseQRCodePayloads && isSolutionsQRCodePayload(rawValue)) {
      if (shouldParseSolutionsQRCodePayload(rawValue)) {
        enqueueQRCodeParse(rawValue)
      }

      return
    }

    qrCodeParseRequestId.current += 1
    recentQRCodeInput.current = ''
    setParseLoading(false)
    onChange(sanitizeOperationalSku(rawValue))
  }

  const qrCodeKeyPressHandler = e => {
    const nextKey = e.key === 'Enter' ? '' : e.key
    const timeSinceLastKeyPress = e.timeStamp - (lastQRCodeKeyPressTimestamp.current || 0)
    const input =
      timeSinceLastKeyPress > QR_CODE_MAX_KEY_PRESS_INTERVAL ? nextKey : `${recentQRCodeInput.current}${nextKey}`

    if (!input.startsWith('@')) {
      recentQRCodeInput.current = ''
      lastQRCodeKeyPressTimestamp.current = e.timeStamp
      return
    }

    e.preventDefault()
    recentQRCodeInput.current = input
    lastQRCodeKeyPressTimestamp.current = e.timeStamp

    if (shouldParseSolutionsQRCodePayload(input)) {
      enqueueQRCodeParse(input)
    }
  }

  const newSku = async () => {
    setLoading(true)
    try {
      const res = await Client.newSku(supplierId)
      onChange(res.data.sku)
    } catch (_error) {
      showToast('Could not generate SKU, please try again', Intent.WARNING, false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bp5-input-group" {...props}>
      <input
        placeholder={placeholder || 'Barcode'}
        value={value || ''}
        onChange={e => changeHandler(e.target.value)}
        onKeyPress={parseQRCodePayloads ? qrCodeKeyPressHandler : undefined}
        className="bp5-input"
        {...inputProps}
      />
      <Button
        loading={loading}
        disabled={!!value}
        onClick={newSku}
        minimal
        intent={Intent.WARNING}
        icon={IconNames.BARCODE}
        style={{ marginTop: 5 }}
      />
    </div>
  )
}

export default React.memo(BarcodeInput)
